import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Box } from '@material-ui/core';
import CustomizeLayoutQuiz from 'component/layout_quiz/layout_quiz';
import { SelectItemOverlay } from 'component/layout_quiz/select_item_overlay';
import { useAuth } from 'contexts/AuthContext';
import { useEffect, useMemo, useState, useRef } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { setPatternDefault } from 'redux/authentication/user_detail';
import { EQuestionId, quizContextType } from 'types/quiz';
import { getPatternQuizId, getPatternSubject } from 'utils/utils';
import { CREATE_ANSWER } from '../../../graph_ql/getQuiz/mutation';
import { GET_QUESTION_DATA, GET_QUIZ_RESPONSE } from '../../../graph_ql/getQuiz/queries';
import { useGetGender } from '../../../hooks/useGetGender';
import { PATH } from '../../../routes/constant';
import { useStyles } from './styles';
import useValidateAnswers from '../hooks/useValidateAnswers';
export interface IPatternProps {}

const Pattern = () => {
    const { currentUser } = useAuth();
    const history = useHistory();
    const gender = useGetGender(false);
    const location = useLocation();
    const dispatch = useDispatch();
    const classes = useStyles();
    const [answer, setAnswer] = useState<any>(null);
    const [answerLoading, setAnswerLoading] = useState<boolean>(false);
    const [requiredQuestion, setRequiredQuestion] = useState(true);
    const [defaultPattern, setDefaultPattern] = useState<any>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [quizData, setQuizData] = useState<any>(null);
    const { userName } = useParams<{ userName: string }>();
    const isUpdate = location.pathname.includes(PATH.MY_STYLE_PREFERENCES);
    const [answerAlreadyUpdated, setAnswerUpdated] = useState(false);

    const [getQuizResponse, { loading: loadingQuiz }] = useLazyQuery(GET_QUIZ_RESPONSE, {
        onCompleted(data) {
            setRequiredQuestion(data.getQuizResponse?.questionsInfo[0]?.required);
            setAnswer(data.getQuizResponse?.answers?.pattern_question);
            setDefaultPattern(data.getQuizResponse?.answers?.pattern_question);
            setQuizData(data.getQuizResponse);
        },
    });

    const createAnswer = (input: {
        questionId: String;
        questionSubject: String;
        contextType: String;
        contextId: String;
        value: String;
        valueType: String;
        isSkipped?: boolean;
    }) => {
        const variables = {
            input,
        };
        setAnswerLoading(true);
        answerMutation({ variables });
    };
    const [answerMutation] = useMutation(CREATE_ANSWER, {
        onCompleted(response) {
            onAnswerCompleted(response);
            dispatch(setPatternDefault(response.createAnswer));
        },
        onError(error) {
            console.log('Create Failed: ', error);
        },
    });

    const onAnswerCompleted = async (response: any) => {
        await getQuizResponse({
            variables: {
                input: {
                    quizId: getPatternQuizId(gender),
                    contextType: quizContextType,
                    contextId: getPatternQuizId(gender),
                    userId: currentUser.uid || gender?.id,
                },
            },
        });
        setAnswerLoading(false);
        if (location.pathname === PATH.QUIZ_MANAGER) {
            history.push(`${PATH.QUIZ_MANAGER}/?signup=${Date.now()}`);
        }
    };
    const { data: patternData, loading } = useQuery(GET_QUESTION_DATA, {
        variables: {
            input: {
                subject: getPatternSubject(gender),
                paging: { page: 0, size: 0 },
                enabled: true,
            },
        },
        skip: !gender,
    });

    const listPatterns = useMemo(
        () =>
            (patternData?.getQuestionData?.data || []).map((item) => {
                return {
                    id: item.id,
                    value: item.value,
                    title: item.title,
                    subject: item.subject,
                    imageUrl: item.imageUrl,
                };
            }),
        [patternData?.getQuestionData?.data],
    );
    const [listPatternOptions, setListPatternOptions] = useState(listPatterns || []);

    const isValidAnswer = useValidateAnswers(
        isUpdate,
        listPatternOptions.filter((item) => item.isSelect).length > 0,
        answerAlreadyUpdated,
    );

    useEffect(() => {
        if (currentUser || gender) {
            getQuizResponse({
                variables: {
                    input: {
                        quizId: getPatternQuizId(gender),
                        contextType: quizContextType,
                        contextId: getPatternQuizId(gender),
                        userId: currentUser.uid || gender?.id,
                    },
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser.uid, gender]);

    useEffect(() => {
        let listPatternsCurrent: any = [];
        if (listPatterns && listPatterns.length > 0 && !loadingQuiz) {
            listPatternsCurrent = [...listPatterns];
            const selectedPatterns = listPatternsCurrent.map((item) => {
                if (defaultPattern?.value.includes(item.value)) {
                    return { ...item, isSelect: true };
                }
                return { ...item, isSelect: false };
            });
            setListPatternOptions(selectedPatterns);
        }
    }, [defaultPattern, defaultPattern?.value, listPatterns, loadingQuiz]);

    const handleSelectItem = (item) => {
        setAnswerUpdated(true);
        setListPatternOptions(
            listPatternOptions.map((option) => {
                if (
                    (item.id === 'question_data_pattern_male_10' || item.id === 'question_data_pattern_female_10') &&
                    !item.isSelect
                ) {
                    option.isSelect = false;
                } else if (
                    (item.id !== 'question_data_pattern_male_10' || item.id !== 'question_data_pattern_female_10') &&
                    (option.id === 'question_data_pattern_male_10' || option.id === 'question_data_pattern_female_10')
                ) {
                    option.isSelect = false;
                }
                if (item.id === option.id) {
                    option.isSelect = option.isSelect ? false : true;
                }
                return option;
            }),
        );
    };
    const input = {
        questionId: EQuestionId.PATTERN,
        questionSubject: getPatternSubject(gender),
        contextType: quizContextType,
        contextId: getPatternQuizId(gender),
        valueType: 'STRING',
        isSkipped: false,
        id: answer?.id,
    };
    const handleClickAnswer = () => {
        const currentItemSelected = listPatternOptions.filter((item) => item.isSelect);
        if (currentItemSelected.length) {
            createAnswer({ ...input, value: currentItemSelected.map((item) => item.value).toString() });
        }
    };
    const handleSkip = () => {
        createAnswer({ ...input, value: '', isSkipped: true });
    };

    const getGridRepeat = () => {
        if (window.innerHeight < 690) {
            return Math.ceil(listPatternOptions.length / 1);
        } else {
            return Math.ceil(listPatternOptions.length / 2);
        }
    };

    return (
        <CustomizeLayoutQuiz
            quizId={getPatternQuizId(gender)}
            title={Boolean(!userName) ? quizData?.subTitle : ''}
            titlePage={quizData?.title}
            handleAnswer={handleClickAnswer}
            isUpdate={location.pathname.includes(PATH.MY_STYLE_PREFERENCES)}
            isDisplayContent={true}
            hideBottomNav={location.pathname === PATH.QUIZ_MANAGER}
            requiredQuestion={requiredQuestion}
            handleSkipQuestion={handleSkip}
            nextBtnDisabled={!isValidAnswer}
            loading={answerLoading}
            containerLoading={loadingQuiz || loading}
        >
            <Box
                display={'flex'}
                flexDirection={'column'}
                textAlign={'center'}
                width={'100%'}
                height={'100%'}
                padding={'10px'}
            >
                <Box className={classes.quizRowContainer}>
                    <ScrollContainer
                        innerRef={listRef}
                        className={classes.quizRow}
                        style={{
                            gridTemplateColumns: `repeat(${getGridRepeat()}, 1fr)`,
                            paddingLeft: '16px',
                            overflowX: 'scroll',
                        }}
                    >
                        {listPatternOptions.length > 0 &&
                            !loading &&
                            listPatternOptions?.map((item, index) => (
                                <div
                                    key={index}
                                    style={{
                                        width: '140px',
                                        marginRight: '10px',
                                        marginTop: '10px',
                                    }}
                                >
                                    <div
                                        style={{
                                            borderRadius: '10px',
                                            background: item.isSelect ? '#1A1A1A' : 'transparent',
                                            transition: 'background 0.3s',
                                            padding: '4px',
                                        }}
                                        onClick={() => handleSelectItem(item)}
                                    >
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '90px',
                                                backgroundImage: `url(${item.imageUrl})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'top',
                                                borderRadius: '10px',
                                                position: 'relative',
                                            }}
                                        >
                                            {item.isSelect && <SelectItemOverlay icon="cross" />}
                                        </div>
                                        <div
                                            className={classes.itemTitle}
                                            style={{
                                                marginTop: '5px',
                                                color: item.isSelect ? '#F2F2F2' : '#1A1A1A',
                                            }}
                                        >
                                            {item.title}
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </ScrollContainer>
                </Box>
            </Box>
        </CustomizeLayoutQuiz>
    );
};
export default Pattern;
